import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const JobCard = ({ job, onApply, onBookmark, isBookmarked, hasApplied }) => {
  const [showDetails, setShowDetails] = useState(false);

  const formatSalary = (min, max) => {
    if (min === 0 && max === 0) return 'Unpaid';
    if (min === max) return `₹${min} LPA`;
    return `₹${min} - ₹${max} LPA`;
  };

  const formatDeadline = (date) => {
    const deadline = new Date(date);
    const today = new Date();
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `${diffDays} days left`;
    return deadline?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getJobTypeColor = (type) => {
    const colors = {
      'Full Time': 'bg-primary/10 text-primary',
      'Internship': 'bg-accent/10 text-accent',
      'Part Time': 'bg-secondary/10 text-secondary',
      'Contract': 'bg-warning/10 text-warning'
    };
    return colors?.[type] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 hover:shadow-warm-md transition-smooth">
      <div className="flex items-start gap-3 md:gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden bg-muted">
          <Image
            src={job?.companyLogo}
            alt={job?.companyLogoAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-base md:text-lg font-heading font-semibold text-card-foreground line-clamp-1">
              {job?.title}
            </h3>
            <button
              onClick={() => onBookmark(job?.id)}
              className="flex-shrink-0 p-2 rounded-md hover:bg-muted transition-smooth"
              aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              <Icon
                name={isBookmarked ? 'Bookmark' : 'Bookmark'}
                size={20}
                color={isBookmarked ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
                strokeWidth={isBookmarked ? 0 : 2}
                fill={isBookmarked ? 'var(--color-accent)' : 'none'}
              />
            </button>
          </div>
          <p className="text-sm md:text-base text-card-foreground font-medium mb-1">
            {job?.company}
          </p>
          <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="Star" size={14} color="var(--color-accent)" fill="var(--color-accent)" />
              <span>{job?.rating}</span>
            </div>
            <span>•</span>
            <span>{job?.reviews} reviews</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getJobTypeColor(job?.type)}`}>
          {job?.type}
        </span>
        {job?.featured && (
          <span className="px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-success/10 text-success">
            Featured
          </span>
        )}
        {job?.urgent && (
          <span className="px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-error/10 text-error">
            Urgent Hiring
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
          <span className="line-clamp-1">{job?.location}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Briefcase" size={16} color="var(--color-muted-foreground)" />
          <span>{job?.experience}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="IndianRupee" size={16} color="var(--color-muted-foreground)" />
          <span>{formatSalary(job?.salaryMin, job?.salaryMax)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
          <span>{formatDeadline(job?.deadline)}</span>
        </div>
      </div>
      <div className="mb-4">
        <p className="text-sm text-card-foreground line-clamp-2 mb-2">
          {job?.description}
        </p>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-primary hover:text-primary/80 font-medium transition-smooth"
        >
          {showDetails ? 'Show less' : 'Show more'}
        </button>
      </div>
      {showDetails && (
        <div className="mb-4 p-3 md:p-4 bg-muted/50 rounded-lg space-y-3">
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-2">Key Requirements:</h4>
            <ul className="space-y-1">
              {job?.requirements?.map((req, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>{req}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-card-foreground mb-2">Skills Required:</h4>
            <div className="flex flex-wrap gap-2">
              {job?.skills?.map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-card border border-border rounded text-xs text-card-foreground"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
        <Button
          variant={hasApplied ? 'outline' : 'default'}
          onClick={() => onApply(job)}
          disabled={hasApplied}
          fullWidth
          iconName={hasApplied ? 'Check' : 'Send'}
          iconPosition="left"
        >
          {hasApplied ? 'Applied' : 'Apply Now'}
        </Button>
        <Button
          variant="outline"
          onClick={() => setShowDetails(!showDetails)}
          fullWidth
          iconName="Eye"
          iconPosition="left"
        >
          View Details
        </Button>
      </div>
    </div>
  );
};

export default JobCard;